import React, { useMemo } from "react";
import dayjs from "dayjs";
import {
  Badge,
  Box,
  Flex,
  IconButton,
  ScrollArea,
  Separator,
  TextArea,
  Text,
} from "@radix-ui/themes";
import { ArrowUpIcon, ChevronLeftIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { MessageAvatarSVG } from "../loadSVG";
import { TenantUserTicket } from "../../entities/TenantUserTicket";
import { getAuthorInitials } from "../../entities/Message";
import { TicketCollaborators } from "./TicketCollaborators";
import { MessageText } from "../message/MessageText";
import { FileMessageComponent } from "../message/FileMessageComponent";
import type { ChatSettingsV2 } from "../../entities/ChatSettingsV2";

interface TicketComment {
  id: string;
  body: string;
  createdAt: string;
  attachmentUrls?: string[];
  agent: boolean;
}

function shouldShowCommentAvatar(current: TicketComment, next?: TicketComment) {
  if (next == null) {
    return true;
  }

  return current.agent !== next.agent;
}

function TicketCommentsLoader({ smallPadding }: { smallPadding?: boolean }) {
  return (
    <div
      className="qc-Chat__BouncingLoader"
      style={{ paddingInline: smallPadding ? "8px" : "36px" }}
    >
      <div />
      <div />
      <div />
    </div>
  );
}

function getAttachmentName(url: string) {
  const name = url.split("?")?.[0]?.split("/")?.pop();
  return name && name.length > 0 ? name : "Attachment";
}

function TicketCommentMessage({
  comment,
  isAgent,
  agentInitials,
  userInitials,
  showAvatar,
}: {
  comment: TicketComment;
  isAgent: boolean;
  agentInitials: string;
  userInitials: string;
  showAvatar: boolean;
}) {
  const textBoxStyle = isAgent
    ? {
      color: "var(--agentText)",
      background: "var(--agentBackground)",
      padding: "8px 12px",
      borderRadius: "8px",
      borderBottomLeftRadius: "2px",
    }
    : {
      color: "var(--userText)",
      padding: "2px 8px",
    };

  const avatarProps = isAgent
    ? {
      kind: "agent" as const,
      initials: agentInitials,
      alt: "Agent Avatar",
    }
    : {
      kind: "user" as const,
      initials: userInitials,
      alt: "User Avatar",
    };

  return (
    <Flex
      gap={"3"}
      align={"end"}
      direction={isAgent ? "row" : "row-reverse"}
      style={{
        paddingLeft: showAvatar && isAgent ? "0" : "36px",
        paddingRight: showAvatar && !isAgent ? "0" : "36px",
      }}
    >
      {showAvatar && <MessageAvatarSVG {...avatarProps} cssOverrides={{ marginBottom: "22px" }} />}

      <Flex direction={"column"} align={isAgent ? "start" : "end"} gap={"1"}>
        <Box style={textBoxStyle}>
          <MessageText text={comment.body} />
        </Box>

        <Text size={"1"} color={"gray"}>
          {dayjs(comment.createdAt).format("DD MMM YYYY hh:mm A")}
        </Text>
      </Flex>
    </Flex>
  );
}

function TicketCommentComposer() {
  return (
    <Box
      style={{
        background: "var(--color-panel-solid)",
        borderRadius: "16px",
        boxShadow: "var(--shadow-2)",
        border: "1px solid var(--gray-a3)",
      }}
    >
      <Flex py={"2"} px={"3"} gap={"2"} justify={"between"} align={"end"}>
        <TextArea
          placeholder="Reply..."
          style={{
            flexGrow: "1",
            boxShadow: "none",
            outline: "none",
          }}
          className="qc-Chat__Input"
          size={"2"}
          aria-label="Reply"
        />
        <IconButton
          color="gray"
          variant="solid"
          radius="full"
          disabled
          className="qc-Chat__CTAButton"
          aria-label="Send message"
        >
          <ArrowUpIcon width="15" height="15" />
        </IconButton>
      </Flex>
    </Box>
  );
}

interface TicketViewProps {
  settings: ChatSettingsV2;
  ticket: TenantUserTicket;
  onBack: () => void;
  comments?: TicketComment[];
  isLoading?: boolean;
  userEmail?: string;
}

export function TicketView({
  settings,
  ticket,
  onBack,
  comments,
  isLoading = false,
  userEmail,
}: TicketViewProps) {
  const sortedComments = useMemo<TicketComment[]>(() => {
    if (!comments || comments.length === 0) {
      return [];
    }

    return [...comments].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [comments]);

  const agentInitials =
    getAuthorInitials(ticket.publicCollaborators?.[0]?.name ?? "Agent") ?? "A";

  const userInitials = getAuthorInitials(userEmail ?? "") ?? "U";

  return (
    <Flex flexGrow={"1"} p={"4"} direction="column" gap="4" style={{ minHeight: 0 }}>
      <Flex direction="column" gap="4" style={{ flexGrow: 1, minHeight: 0 }}>
        <IconButton
          variant="outline"
          color="gray"
          onClick={onBack}
          aria-label="Back to chat"
        >
          <ChevronLeftIcon />
        </IconButton>

        <Flex direction="column">
          <Text size={"3"} color={"gray"} weight={"medium"} highContrast>
            {ticket.subject}
          </Text>

          <Flex gap="3" align="center">
            <Text size={"1"} color={"gray"}>
              {`Open since ${dayjs(ticket.createdAt).format("DD MMM YYYY")}`}
            </Text>

            <Separator orientation="vertical" />

            <Badge
              color="gray"
              variant="soft"
              size="1"
              style={{ textTransform: "capitalize" }}
            >
              {ticket.status}
            </Badge>
          </Flex>

          <TicketCollaborators collaborators={ticket.publicCollaborators} />
        </Flex>

        <Flex direction="column" gap={"2"} style={{ flexGrow: 1, minHeight: 0 }}>
          <ScrollArea style={{ width: "100%", flexGrow: 1 }}>
            <Flex
              direction="column"
              gap={"3"}
              className="qc-Chat__Messages_Viewport"
              style={{ paddingBottom: "88px" }}
              role="log"
            >
              {isLoading && (
                <Flex p={"4"}>
                  <TicketCommentsLoader smallPadding />
                </Flex>
              )}

              {!isLoading && sortedComments.length === 0 && (
                <Text size={"1"} color={"gray"} style={{ textAlign: "center" }}>
                  No comments yet.
                </Text>
              )}

              {!isLoading &&
                sortedComments.map((comment, index) => (
                  <React.Fragment key={comment.id}>
                    <TicketCommentMessage
                      comment={comment}
                      isAgent={comment.agent}
                      agentInitials={agentInitials}
                      userInitials={userInitials}
                      showAvatar={shouldShowCommentAvatar(comment, sortedComments[index + 1])}
                    />
                    {comment.attachmentUrls?.map((url) => (
                      <FileMessageComponent
                        key={`${comment.id}-${url}`}
                        url={url}
                        filename={getAttachmentName(url)}
                        createdAt={comment.createdAt}
                        showTimestamp={false}
                        orientation={comment.agent ? "left" : "right"}
                        isLastMessage={false}
                        thinking={false}
                      />
                    ))}
                  </React.Fragment>
                ))}
            </Flex>
          </ScrollArea>
        </Flex>
      </Flex>


      {settings.layout?.supportTickets?.allowUserReplyToTicket && (
        <Box
          style={{
            position: "sticky",
            zIndex: 1,
          }}
        >
          <TicketCommentComposer />

          <Flex align="center" justify="center" gap="2" mt="2">
            <InfoCircledIcon color="gray" />
            <Text size={"1"} color={"gray"}>A human agent may take some time to review and respond.</Text>
          </Flex>
        </Box>
      )}
    </Flex>
  );
}
